import { useState } from 'react';
import {
  AlarmFilterCluster,
  AlarmRollupBanner,
  AlarmRow,
  DEFAULT_FILTER_VALUE,
  RunningLogCard,
  SeverityPill,
  type AlarmDisplayRow,
  type FilterValue,
} from '@/components/alarms/primitives';
import type { AlarmHistoryEvent } from '@/types/alarm.types';

const NOW = Date.now();

const MOCK_ALARMS: AlarmDisplayRow[] = [
  {
    id: 'gen-3270:gov',
    severity: 'warning',
    status: 'needs_attention',
    description: 'Governor Output Limit',
    equipment: 'GEN-3270',
    equipmentType: 'generator',
    occurredAt: new Date(NOW - 5 * 60 * 1000),
    deviceId: 'GEN-3270',
  },
  {
    id: 'pcs-001:overtemp',
    severity: 'critical',
    status: 'needs_attention',
    description: 'PCS Inverter Overtemperature',
    equipment: 'PCS-001',
    equipmentType: 'PCS',
    occurredAt: new Date(NOW - 45 * 1000),
    deviceId: 'PCS-001',
  },
  {
    id: 'bms-001:cell-imbalance',
    severity: 'info',
    status: 'acknowledged',
    description: 'Rack 3 Cell Voltage Imbalance',
    equipment: 'BMS-001',
    equipmentType: 'BMS',
    occurredAt: new Date(NOW - 2 * 3600 * 1000),
    deviceId: 'BMS-001',
    ackedBy: 'demo@djin.com',
    ackedAt: new Date(NOW - 30 * 60 * 1000),
    stale: true,
  },
];

const MOCK_EVENTS: AlarmHistoryEvent[] = [
  {
    event_id: 'ev-1',
    site_id: 'hondo',
    device_id: 'GEN-3270',
    condition_key: 'gov-output-limit',
    device_type: 'generator',
    block_id: 'b1',
    transition: 'raised',
    severity: 'warning',
    category: 'fault',
    ts: new Date(NOW - 1 * 60 * 1000).toISOString(),
    condition_label: 'Governor Output Limit',
    value: '1',
    metadata: {},
    lifecycle: null,
  },
  {
    event_id: 'ev-2',
    site_id: 'hondo',
    device_id: 'GEN-3266',
    condition_key: 'gov-output-limit',
    device_type: 'generator',
    block_id: 'b2',
    transition: 'cleared',
    severity: 'warning',
    category: 'fault',
    ts: new Date(NOW - 6 * 60 * 1000).toISOString(),
    condition_label: 'Governor Output Limit',
    value: '0',
    metadata: {},
    lifecycle: null,
  },
  {
    event_id: 'ev-3',
    site_id: 'hondo',
    device_id: 'PCS-001',
    condition_key: 'overtemp',
    device_type: 'pcs',
    block_id: 'b1',
    transition: 'acknowledged',
    severity: 'critical',
    category: 'fault',
    ts: new Date(NOW - 12 * 60 * 1000).toISOString(),
    condition_label: 'PCS Inverter Overtemperature',
    value: '1',
    metadata: {},
    lifecycle: null,
  },
];

export function AlarmPrimitivesPreview() {
  const [filter, setFilter] = useState<FilterValue>(DEFAULT_FILTER_VALUE);

  return (
    <div className="p-4 space-y-8 bg-ems-bg min-h-screen text-ems-text-primary">
      <BreakpointIndicator />

      <Section title="SeverityPill — pill variant (default)">
        <div className="flex gap-2 flex-wrap">
          <SeverityPill severity="critical" />
          <SeverityPill severity="warning" />
          <SeverityPill severity="info" />
        </div>
      </Section>

      <Section title="SeverityPill — dot variant">
        <div className="flex gap-2 items-center">
          <SeverityPill severity="critical" variant="dot" />
          <SeverityPill severity="warning" variant="dot" />
          <SeverityPill severity="info" variant="dot" />
        </div>
      </Section>

      <Section title="SeverityPill — icon variant">
        <div className="flex gap-2 items-center">
          <SeverityPill severity="critical" variant="icon" />
          <SeverityPill severity="warning" variant="icon" />
          <SeverityPill severity="info" variant="icon" />
        </div>
      </Section>

      <Section title="AlarmRow — default variant">
        <div className="space-y-2">
          {MOCK_ALARMS.map((a) => (
            <AlarmRow
              key={a.id}
              alarm={a}
              variant="default"
              now={NOW}
              onClick={(x) => console.log('clicked', x.id)}
            />
          ))}
        </div>
      </Section>

      <Section title="AlarmRow — compact variant">
        <div className="space-y-1">
          {MOCK_ALARMS.map((a) => (
            <AlarmRow key={a.id} alarm={a} variant="compact" now={NOW} />
          ))}
        </div>
      </Section>

      <Section title="AlarmRow — dense variant">
        <div className="space-y-1">
          {MOCK_ALARMS.map((a) => (
            <AlarmRow key={a.id} alarm={a} variant="dense" now={NOW} />
          ))}
        </div>
      </Section>

      <Section title="AlarmRollupBanner — site scope">
        <AlarmRollupBanner
          scope="site"
          name="Hondo Microgrid"
          counts={{ critical: 1, warning: 5, info: 0, acked: 2, stale: 0 }}
        />
      </Section>

      <Section title="AlarmRollupBanner — site scope, quiet">
        <AlarmRollupBanner
          scope="site"
          name="Hondo Microgrid"
          counts={{ critical: 0, warning: 0, info: 0, acked: 0, stale: 0 }}
        />
      </Section>

      <Section title="AlarmRollupBanner — block scope">
        <AlarmRollupBanner
          scope="block"
          name="GEN-3270"
          counts={{ critical: 0, warning: 1, info: 0, acked: 0, stale: 0 }}
        />
      </Section>

      <Section title="RunningLogCard — populated">
        <RunningLogCard events={MOCK_EVENTS} onViewAll={() => console.log('view all')} />
      </Section>

      <Section title="RunningLogCard — empty">
        <RunningLogCard events={[]} />
      </Section>

      <Section title="AlarmFilterCluster — expanded variant">
        <AlarmFilterCluster value={filter} onChange={setFilter} variant="expanded" />
        <pre className="text-xs text-ems-text-muted mt-2 p-2 bg-ems-bg-card rounded border border-ems-border overflow-x-auto">
          {JSON.stringify(filter, null, 2)}
        </pre>
      </Section>

      <Section title="AlarmFilterCluster — compact variant">
        <AlarmFilterCluster
          value={filter}
          onChange={setFilter}
          variant="compact"
          persistInUrl={false}
        />
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-sm font-semibold uppercase tracking-wider text-ems-text-secondary mb-2">
        {title}
      </h2>
      {children}
    </section>
  );
}

function BreakpointIndicator() {
  return (
    <div className="fixed top-2 right-2 z-50 px-2 py-1 rounded bg-ems-bg-card border border-ems-border text-xs text-ems-text-muted font-mono">
      <span className="sm:hidden">375 (xs)</span>
      <span className="hidden sm:inline md:hidden">640 (sm)</span>
      <span className="hidden md:inline lg:hidden">768 (md)</span>
      <span className="hidden lg:inline xl:hidden">1024 (lg)</span>
      <span className="hidden xl:inline">1280+ (xl)</span>
    </div>
  );
}
